import { useTranslation } from "react-i18next";
import type { OtterfolioOverview } from "../../lib/otterfolio";
import { OTTERFOLIO_THEME } from "./theme";

interface Props {
  overview: OtterfolioOverview;
}

function fmtEur(cents: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
}

function fmtPct(percent: number, digits = 1): string {
  const sign = percent >= 0 ? "+" : "";
  return `${sign}${percent.toFixed(digits)} %`;
}

function truncate(s: string, max = 14): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

interface Kpi {
  label: string;
  value: string;
  color?: string;
}

const RESPONSIVE_STYLE = `
  @media (min-width: 1024px) {
    .otterfolio-kpi-strip {
      grid-template-columns: repeat(6, 1fr) !important;
    }
  }
`;

export function KpiStrip({ overview }: Props) {
  const { t } = useTranslation();
  const okColor = OTTERFOLIO_THEME.primary;
  const badColor = OTTERFOLIO_THEME.highlight;

  const kpis: Kpi[] = [
    {
      label: t("otterfolio.kpi.depotValue", { defaultValue: "Depotwert" }),
      value: fmtEur(overview.totalValueCents),
    },
    {
      label: t("otterfolio.kpi.sinceStart", { defaultValue: "Seit Start" }),
      value: fmtPct(overview.returnPercent * 100),
      color: overview.returnPercent >= 0 ? okColor : badColor,
    },
    {
      label: t("otterfolio.kpi.felsPerf", { defaultValue: "🪨 Fels-Perf" }),
      value: `${fmtPct(overview.fels.weightedReturnPercent)} · ${fmtEur(overview.fels.valueCents)}`,
      color: overview.fels.weightedReturnPercent >= 0 ? okColor : badColor,
    },
    {
      label: t("otterfolio.kpi.raketePerf", { defaultValue: "🚀 Rakete-Perf" }),
      value: `${fmtPct(overview.rakete.weightedReturnPercent)} · ${fmtEur(overview.rakete.valueCents)}`,
      color: overview.rakete.weightedReturnPercent >= 0 ? okColor : badColor,
    },
    {
      label: t("otterfolio.kpi.best", { defaultValue: "Best" }),
      value: overview.best
        ? `${truncate(overview.best.name)} · ${fmtPct(overview.best.gainLossPercent)}`
        : "—",
      color: okColor,
    },
    {
      label: t("otterfolio.kpi.worst", { defaultValue: "Worst" }),
      value: overview.worst
        ? `${truncate(overview.worst.name)} · ${fmtPct(overview.worst.gainLossPercent)}`
        : "—",
      color: badColor,
    },
  ];

  return (
    <>
      <style>{RESPONSIVE_STYLE}</style>
      <div
        className="otterfolio-kpi-strip"
        style={{
          background: OTTERFOLIO_THEME.cardBg,
          border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
          borderRadius: 12,
          padding: 16,
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 12,
        }}
      >
        {kpis.map((k) => (
          <div key={k.label}>
            <div
              style={{
                color: OTTERFOLIO_THEME.textMuted,
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {k.label}
            </div>
            <div
              style={{
                color: k.color ?? OTTERFOLIO_THEME.text,
                fontWeight: 700,
                fontSize: 14,
                marginTop: 2,
              }}
            >
              {k.value}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
